import {
  BareButton,
  ChevronRightIcon,
  formatDate,
  formatMoney,
} from "@setel/web-ui";
import Badge from "~/lib/components/ui/Badge";
import { useSearchParams } from "@remix-run/react";
import { LocaleLink, useLocaleNavigate } from "~/lib/components/link";
import React from "react";
import { matchAnyInArray } from "~/lib/utils/helper/matchAnyInArray";
import { MesraTransactionItem } from "~/lib/types/loyalty.type";
import { ITransactionV3 } from "~/lib/types/payment.type";
import { trackUserEvent } from "../../../utils/analytics";
import { EVENT_CHANNELS, EVENT_TYPES } from "../../../utils/analytics.events";
import { useTranslation } from "../../../utils/store/useTranslation";
import { useAuth } from "~/lib/utils/hooks/useAuth";
import { mapReferenceTypeToDisplay } from "~/lib/utils/helper/mapReferenceType";
import { useChat } from "~/lib/utils/store/useChatContext";
import { REFERENCE_TYPES } from "~/lib/utils/const";
import { sendZendeskEvent } from "~/lib/utils/zendesk";

type ITransactionCardProps = {
  transaction: ITransactionV3 | MesraTransactionItem;
  link?: string;
  hasHeader?: boolean;
  header?: string;
  pricePosition?: "right" | "bottom";
};

type ITransactionCardDetailProps = {
  transaction: ITransactionV3 | MesraTransactionItem;
  tags: string[];
};

const renderAmount = (
  amountTransaction: string,
  transaction: ITransactionV3 | MesraTransactionItem,
  isOverview?: boolean
) => {
  const additionalClassName = isOverview
    ? "font-medium text-base font-secondary"
    : "font-title font-bold text-article-title leading-[56px]";

  if (!transaction.type || transaction.status === "failed") {
    return <div className={additionalClassName}>{amountTransaction}</div>;
  }

  if (
    [
      "TOPUP_REFUND",
      "TOPUP",
      "REFUND",
      "TOPUP_TO_PAY_REFUND",
      "REVERSED",
      // for loyalty
      "EARN",
      "EXTERNALEARN",
    ].includes(transaction.type.toUpperCase())
  ) {
    return (
      <div className={`text-success-700 ${additionalClassName}`}>
        +{amountTransaction}
      </div>
    );
  }

  if (
    [
      "PURCHASE",
      "AUTHORIZE",
      "CAPTURE",
      "CANCEL",
      "PURCHASE_EXTERNAL",
      "TOPUP_TO_PAY",
      // for loyalty
      "REDEEM",
      "EXTERNALREDEEM",
    ].includes(transaction.type.toUpperCase())
  ) {
    return (
      <div className={`text-black ${additionalClassName}`}>
        -{amountTransaction}
      </div>
    );
  }

  return <div className={additionalClassName}>{amountTransaction}</div>;
};

const getIconSrc = (transaction: ITransactionV3 | MesraTransactionItem) => {
  const iconSrc = REFERENCE_TYPES.find(
    (item) =>
      item.label.toLowerCase() === transaction?.referenceType?.toLowerCase()
  )?.icon;

  if (iconSrc) {
    return iconSrc;
  }

  if (transaction.isMesra) {
    return "/images/use-cases/mesra-reward.svg";
  }
};

export const TransactionCardDetail = ({
  transaction,
  tags,
}: ITransactionCardDetailProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useLocaleNavigate();
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { isMessagingMode } = useChat();
  const amountTransaction = transaction.isPayment
    ? `RM ${transaction.amount ? formatMoney(transaction.amount) : 0}`
    : `${transaction.amount} pts`;
  const infoTransaction = transaction?.stationName || transaction.message;
  const isDetailPage = searchParams.has("type");
  const referenceTypeTransaction = transaction?.referenceType
    ? mapReferenceTypeToDisplay(transaction?.referenceType)
    : "";
  const descTransaction = transaction?.isPayment
    ? isDetailPage
      ? infoTransaction || referenceTypeTransaction
      : referenceTypeTransaction +
        (infoTransaction ? ` - ${infoTransaction}` : " ")
    : referenceTypeTransaction + " - " + transaction?.merchantName;

  const transactionName =
    (transaction && referenceTypeTransaction) || transaction?.title;
  const payload =
    `ID: ${transaction?.id}` +
    `\nName: ${transactionName}` +
    `\nDescription: ${descTransaction}` +
    `\nStatus: ${transaction?.status}` +
    `\nDate: ${transaction?.createdAt}` +
    `\nPrice: ${amountTransaction}`;

  const sendReport = () => {
    trackUserEvent(
      EVENT_TYPES.WEB_SUPPORT_HELP_CENTRE_REPORT_A_PROBLEM_CHAT,
      {
        reference_type: transaction?.referenceType,
      },
      [EVENT_CHANNELS.MIXPANEL]
    );

    if (!window?.zE || !profile) return;

    if (isMessagingMode) {
      sendZendeskEvent({
        event: ["messenger", "show"],
      });
      sendZendeskEvent({
        event: ["messenger", "open"],
      });
      sendZendeskEvent({
        event: ["messenger:set", "conversationTags", tags],
      });
    } else {
      sendZendeskEvent({
        event: ["webWidget", "show"],
      });
      sendZendeskEvent({
        event: ["webWidget", "open"],
      });
      sendZendeskEvent({
        event: ["webWidget", "chat:addTags", tags],
      });
      sendZendeskEvent({
        event: [
          "webWidget",
          "identify",
          {
            name: profile.fullName,
            email: profile.email,
          },
        ],
      });
      sendZendeskEvent({
        event: ["webWidget", "chat:send", payload],
      });
    }
  };

  const iconSrc = getIconSrc(transaction);

  return (
    <div className="w-full md:w-[335px] border border-gray-500 rounded-xl">
      <div className="p-4 flex justify-center items-center flex-col bg-brand-100 min-h-[133px] rounded-t-xl gap-3">
        <div className="w-11 h-11">
          {!!iconSrc && (
            <img
              alt={transaction.referenceType}
              width={44}
              height={44}
              src={iconSrc}
            />
          )}
        </div>
        <div className="flex flex-col items-center gap-1 w-full">
          <div className="text-center font-medium text-[15px] leading-[21px]">
            {descTransaction}
          </div>
          <div className="text-center text-gray-light text-sm">
            {transaction.createdAt && formatDate(transaction?.createdAt)}
          </div>
        </div>
        {!matchAnyInArray(transaction.status, ["success", "successful"]) && (
          <Badge status={transaction.status} />
        )}
      </div>
      <div className="flex flex-col gap-4 justify-center items-center bg-white border-t border-gray-500 border-dashed min-h-[124px] px-[10px] md:px-5">
        <div className="text-gray-dark text-sm">{t("purchaseAmount")}</div>
        <div className="font-title font-bold text-article-title leading-[56px]">
          {renderAmount(amountTransaction, transaction, false)}
        </div>
      </div>
      <div className="min-h-[60px] flex bg-white border-t border-gray-500 rounded-b-xl">
        <BareButton
          className="w-1/2 border-r border-gray-500 text-sm font-medium"
          onClick={sendReport}
        >
          {t("reportTransaction")}
        </BareButton>
        <BareButton
          className="w-1/2 text-sm font-medium"
          onClick={() => navigate("/transaction")}
        >
          {t("moreTransactions")}
        </BareButton>
      </div>
    </div>
  );
};

export const TransactionCardOverview = ({
  transaction,
  link = "",
  pricePosition = "bottom",
}: Pick<ITransactionCardProps, "transaction" | "link" | "pricePosition">) => {
  const [searchParams] = useSearchParams();
  const isDetailPage = searchParams.has("type");
  const infoTransaction = transaction?.stationName || transaction.message;
  const referenceTypeTransaction = transaction?.referenceType
    ? mapReferenceTypeToDisplay(transaction?.referenceType)
    : "";
  const descTransaction = transaction?.isPayment
    ? isDetailPage
      ? infoTransaction || referenceTypeTransaction
      : referenceTypeTransaction +
        (infoTransaction ? ` - ${infoTransaction}` : " ")
    : referenceTypeTransaction + " - " + transaction?.merchantName;

  const amountTransaction = transaction.isPayment
    ? `RM ${transaction.amount ? formatMoney(transaction.amount) : 0}`
    : `${transaction.amount} pts`;

  const iconSrc = getIconSrc(transaction);

  return (
    <LocaleLink to={link}>
      <div
        data-testid="transaction-card-home"
        className="bg-white cursor-pointer flex flex-row items-start justify-between gap-3"
      >
        <div className="h-11 w-11">
          {!!iconSrc && (
            <img
              key="payment"
              src={iconSrc}
              alt={transaction.referenceType}
              width={44}
              height={44}
            />
          )}
        </div>

        <div className="flex-1">
          <p
            className={`font-normal text-[15px] leading-[21px] text-gray-dark block ${
              transaction?.referenceType === "topup" ? "" : "capitalize"
            }`}
          >
            {descTransaction}
          </p>
          <div className="flex flex-row gap-2 mt-1">
            <p className="font-secondary text-gray-light font-normal text-sm">
              {transaction?.createdAt && formatDate(transaction.createdAt)}
            </p>
            {!matchAnyInArray(transaction.status, [
              "success",
              "successful",
            ]) && <Badge status={transaction.status} />}
          </div>

          {pricePosition === "bottom" ? (
            <div className="mt-3">
              {renderAmount(amountTransaction, transaction, true)}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          {pricePosition === "right"
            ? renderAmount(amountTransaction, transaction, true)
            : null}
          <ChevronRightIcon className="text-gray-light w-6 h-6" />
        </div>
      </div>
    </LocaleLink>
  );
};
